import { Button, ContentLayout, Logo, PageLayout } from 'components';
import { useCallback, useEffect } from 'react';
import style from './index.module.scss';
import { WORKER_STATUS } from 'api';
import { useDispatch, useSelector } from 'react-redux';
import { RejectedIcon } from 'components/icons/icons';
import { useAuth } from 'hooks/useAuth';
import { ROUTE } from 'routers/data';
import { useNavigate } from 'react-router-dom';
import { getUser, setAlreadyRedirect } from 'pages/profile/redux-sagas/actions';
import { LoadingOutlined } from '@ant-design/icons';
import { TDocumentations } from 'pages/process-complate-profile';

export type TProfileStatus = {
  status: WORKER_STATUS;
};

export const ProfileStatus = () => {
  const { signOut, getToken } = useAuth();
  const loading = useSelector((state: any) => state.profile?.loading);
  const email = useSelector((state: any) => state.profile?.data?.email);
  const rejectReason = useSelector(
    (state: any) => state.profile?.data?.rejectReason,
  );
  const fieldsWithError = useSelector(
    (state: any) => state.profile?.data?.fieldsWithError,
  );
  const alreadyRedirect = useSelector(
    (state: any) => state.profile?.alreadyRedirect,
  );
  const status = useSelector(
    (state: any) => state.profile?.data?.state,
  ) as TProfileStatus['status'];
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      await getToken(true);
      dispatch(getUser(email));
    })();
  }, [dispatch, email, getToken]);

  const getValues = useCallback(() => {
    switch (status) {
      case WORKER_STATUS.needInformation:
        return {
          title: 'Su perfil no se encuentra completo',
          subtitle: 'Complételo para comenzar a encontrar trabajos',
          message: 'COMPLETAR PERFIL',
        };
      case WORKER_STATUS.waitingRevision:
        return {
          title: 'Se ha enviado exitosamente su solicitud de alta',
          subtitle: 'La aprobación puede tardar unos días, será notificado via email',
          message: 'VER DATOS DE LA SOLICITUD',
        };
      case WORKER_STATUS.rejected:
        return {
          title: 'Su solicitud ha sido rechazada',
          subtitle:
            'Sin embargo, puede modificar sus datos y realizarla nuevamente',
          message: 'MODIFICAR SOLICITUD',
        };
      case WORKER_STATUS.suspended:
        return {
          title: 'Su pefil se encuentra suspendido',
          subtitle:
            "Se ha evaluado su caso y se ha decidido suspender su cuenta, Si cree que se trata de un error, no dude en comunicarse con <a href='mailto:administracion@aqnllamo.com'>administracion@aqll.com</a>",
        };

      case WORKER_STATUS.enabled:
        return {
          title: 'Su solicitud ha sido <b>APROBADA</b>',
        };

      default:
    }
  }, [status]);

  const getStepToRedirect = useCallback(() => {
    if (
      [
        'firstName',
        'lastName',
        'documentTypeId',
        'documentNumber',
        'birthday',
        'phoneNumber',
        'cityId',
        'address',
        'streetNumber',
      ].some((value) => fieldsWithError?.includes(value))
    )
      return 1;
    if (
      ['educationLevelId', 'worksSince', 'workFieldIds'].some((value) =>
        fieldsWithError?.includes(value),
      )
    )
      return 2;
    if (['location'].some((value) => fieldsWithError?.includes(value)))
      return 3;
    if (
      (
        [
          'cuit',
          'enrollment',
          'insurance',
          'goodConduct',
        ] as TDocumentations[number]['file'][]
      ).some((value) => fieldsWithError?.includes(value))
    )
      return 4;
    return 1;
  }, [fieldsWithError]);

  const navProcess = useCallback(() => {
    let redirect;
    if (!alreadyRedirect && status === WORKER_STATUS.rejected) {
      dispatch(setAlreadyRedirect());
      const stepToRedirect = getStepToRedirect();
      redirect = `${ROUTE.complateProfileRoot}/${
        (ROUTE as any)[`complateProfileStep${stepToRedirect}`]
      }`;
    }
    navigate(redirect || ROUTE.complateProfileRoot);
  }, [dispatch, alreadyRedirect, status, navigate, getStepToRedirect]);

  return (
    <PageLayout className={style.status}>
      <ContentLayout>
        <Logo className={style.banner} type="banner" />
        {status === 'needInformation' && (
          <img
            className={style.icon}
            src={'assets/icon/incomplete_profile.svg'}
            alt=""
          />
        )}
        {status === 'waitingRevision' && (
          <img
            className={style.icon}
            src={'assets/icon/waiting_profile.svg'}
            alt=""
          />
        )}
        {['rejected', 'suspended'].includes(status) && (
          <RejectedIcon className={`${style.icon} ${style.red}`} />
        )}
        <div>
          <h1
            className={
              ['rejected', 'suspended'].includes(status) ? style.red : ''
            }
            dangerouslySetInnerHTML={{ __html: getValues()?.title! }}
          />
          <p dangerouslySetInnerHTML={{ __html: rejectReason }} />
          <p dangerouslySetInnerHTML={{ __html: getValues()?.subtitle! }} />

          <div className={style.checking}>
            {loading && (
              <>
                <p>Comprobando Información</p>
                <LoadingOutlined />
              </>
            )}
          </div>
        </div>
        <div className={style.footer}>
          {getValues()?.message && (
            <Button onClick={navProcess} shape="round" buttonType="solid">
              {getValues()?.message}
            </Button>
          )}
          <Button
            onClick={signOut}
            shape="round"
            buttonStyle="red"
            buttonType="outline"
          >
            CERRAR SESSIÓN
          </Button>
        </div>
      </ContentLayout>
    </PageLayout>
  );
};
